import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import AdvanceTable from "components/base/AdvanceTable";
import { Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import LinkProduto from "components/common/LinkProduto";

// Interface para os produtos na lista
export interface ProdutoPedido {
  id: number;
  codigo: string;
  descricao: string;
  quantidade: number;
  precoCompra?: number;
  observacao?: string;
  codigosDeBarras?: string[];
  estoque?: number;
}

// Tipo para as props que serão passadas para as colunas
interface PedidosTableProps {
  onRemoverProduto: (id: number) => void;
  onAtualizarQuantidade: (id: number, quantidade: number) => void;
  onAtualizarPrecoCompra: (id: number, preco: number) => void;
  onAtualizarObservacao: (id: number, observacao: string) => void;
}

export const createPedidosTableColumns = (
  props: PedidosTableProps
): ColumnDef<ProdutoPedido>[] => [
  {
    accessorKey: "codigo",
    header: "Código",
    cell: ({ getValue }) => {
      const codigo = getValue() as string;
      return (        
        <span className="text-body-emphasis fw-medium">{codigo}</span>
      );
    },
    meta: {
      headerProps: { style: { width: "8%" }, className: "ps-4" },
      cellProps: {
        className: "ps-4",
      },
    },
  },
  {
    accessorKey: "descricao",
    header: "Descrição",
    cell: ({ getValue, row }) => {
      const descricao = getValue() as string;
      return (
        <LinkProduto produtoId={row.original.id}>
          <span className="text-body-emphasis fw-medium">{descricao}</span>
        </LinkProduto>
      );
    },
    meta: {
      headerProps: { style: { width: "20%" } },
      cellProps: {
        className: "fs-9",
      },
    },
  },
  {
    accessorKey: "codigosDeBarras",
    header: "Códigos de Barras",
    cell: ({ getValue }) => {
      const codigos = getValue() as string[] | undefined;
      return (
        <div className="d-flex flex-wrap gap-1">
          {codigos && codigos.length > 0 ? (
            codigos.map((codigo, index) => (
              <span
                key={index}
                className="badge bg-secondary-subtle text-secondary-emphasis"
              >
                {codigo}
              </span>
            ))
          ) : (
            <span className="text-body-tertiary small">-</span>
          )}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: "18%" } },
      cellProps: {
        className: "fs-9",
      },
    },
  },
  {
    accessorKey: "estoque",
    header: "Estoque",
    cell: ({ getValue }) => {
      const estoque = getValue() as number | undefined;
      if (estoque === undefined) {
        return <span className="text-body-tertiary small">-</span>;
      }
      return (
        <span
          className={`badge ${
            estoque > 0
              ? "bg-success-subtle text-success-emphasis"
              : "bg-danger-subtle text-danger-emphasis"
          }`}
        >
          {estoque}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: "8%" } },
      cellProps: {
        className: "text-center",
      },
    },
  },
  {
    accessorKey: "quantidade",
    header: "Quantidade",
    cell: ({ getValue, row }) => {
      const quantidade = getValue() as number;
      return (
        <div className="d-flex justify-content-center">
          <Form.Control
            type="number"
            min="1"
            value={quantidade === 0 ? "" : quantidade}
            onFocus={(e) => {
              // Seleciona todo o texto para facilitar a substituição
              setTimeout(() => e.target.select(), 0);
            }}
            onChange={(e) => {
              const valor = e.target.value === "" ? 0 : parseInt(e.target.value);
              props.onAtualizarQuantidade(row.original.id, valor);
            }}
            onBlur={(e) => {
              // Quando sair do campo, se estiver vazio ou 0, define como 1
              const valor = parseInt(e.target.value);
              if (!valor || valor < 1) {
                props.onAtualizarQuantidade(row.original.id, 1);
              }
            }}
            style={{ width: "90px" }}
            className="text-center fw-semibold"
          />
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: "10%" } },
      cellProps: {
        className: "text-center",
      },
    },
  },
  {
    accessorKey: "precoCompra",
    header: "Preço de Compra",
    cell: ({ getValue, row }) => {
      const precoCompra = getValue() as number | undefined;
      const [isEditing, setIsEditing] = React.useState(false);
      const [inputValue, setInputValue] = React.useState("");
      
      const formatCurrency = (value: number | undefined) => {
        if (value === undefined || value === null) return "";
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(value);
      };
      
      return (
        <div className="d-flex justify-content-center align-items-center gap-2">
          <Form.Control
            type="text"
            value={isEditing ? inputValue : formatCurrency(precoCompra)}
            placeholder="R$ 0,00"
            onFocus={(e) => {
              setIsEditing(true);
              setInputValue(precoCompra !== undefined && precoCompra !== null ? precoCompra.toString() : "");
              // Seleciona todo o texto para facilitar a substituição
              setTimeout(() => e.target.select(), 0);
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onBlur={(e) => {
              setIsEditing(false);
              const valor = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
              const numero = valor === "" ? 0 : parseFloat(valor);
              const valorFormatado = Number(numero.toFixed(2));
              props.onAtualizarPrecoCompra(row.original.id, valorFormatado);
            }}
            style={{ width: "110px" }}
            className="text-center fw-semibold"
          />
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-preco-${row.original.id}`}>
                Último preço comprado do produto
              </Tooltip>
            }
          >
            <span style={{ cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="text-body-tertiary"
                style={{ fontSize: "0.85rem" }}
              />
            </span>
          </OverlayTrigger>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: "14%" } },
      cellProps: {
        className: "text-center",
      },
    },
  },
  {
    accessorKey: "observacao",
    header: "Observação",
    cell: ({ getValue, row }) => {
      const observacao = getValue() as string | undefined;
      return (
        <Form.Control
          type="text"
          size="sm"
          placeholder="Adicionar observação..."
          value={observacao || ""}
          onFocus={(e) => {
            // Seleciona todo o texto para facilitar a substituição
            setTimeout(() => e.target.select(), 0);
          }}
          onChange={(e) =>
            props.onAtualizarObservacao(row.original.id, e.target.value)
          }
        />
      );
    },
    meta: {
      headerProps: { style: { width: "16%" } },
      cellProps: {
        className: "fs-9",
      },
    },
  },
  {
    accessorKey: "acoes",
    header: "Ações",
    cell: ({ row }) => {
      return (
        <Button
          variant="link"
          size="sm"
          className="text-danger p-1"
          onClick={() => props.onRemoverProduto(row.original.id)}
          title="Remover produto"
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      );
    },
    meta: {
      headerProps: { style: { width: "6%" } },
      cellProps: {
        className: "text-center",
      },
    },
  },
];

const PedidosTable = () => {
  return (
    <div>
      <AdvanceTable tableProps={{ className: "phoenix-table fs-9" }} />
    </div>
  );
};

export default PedidosTable;
